import React, { useEffect, useState } from "react";

import { getSupaClient, getWorkspace } from "../../lib/supabase";
import { TAG_COLORS } from "../../utils/constants";

/**
 * Custom hook for handling team or guild tags
 * @returns
 * loading - wether the hook is loading
 * teamTags - a list of tagsfor the current team or guild
 * setTeamTags - state modifying function to update the team tags
 * addTag - callback to add a new team tag
 * addBulkTags - function to edit member tags in bulk
 * loadTeamTags - callback to reload the team tags
 * errors - an object with any errors that occur in the hook
 * isBulkEditModalVisible: wether the modal is visible in the parent component
 * setIsBulkEditModalVisible:state modifying function to update the visibilit of the modal
 * updateData: state modifying function to updatethe data
 */
function useTags() {
  const supabase = getSupaClient();
  const platform = localStorage.getItem("platform");
  const workspace = getWorkspace(supabase);
  const teamId =
    platform === "slack"
      ? localStorage.getItem("team-id")
      : localStorage.getItem("guild-id");
  const [loading, setLoading] = useState({
    addTag: false,
    addBulkTags: false,
    loadTeamTags: false,
  });
  const [teamTags, setTeamTags] = useState([]);
  const [errors, setErrors] = useState({
    addTag: false,
    addBulkTags: false,
    loadTeamTags: false,
  });
  const [isBulkEditModalVisible, setIsBulkEditModalVisible] = useState(false);

  /**
   * Loads tags for a given team or guild
   */
  const loadTeamTags = async () => {
    setLoading({ ...loading, loadTeamTags: true });
    setErrors({ ...errors, loadTeamTags: false });
    const teamSelection = platform === "slack" ? "team" : "guild";
    const { data, error } = await supabase
      .from("tags")
      .select("id, name, color")
      .eq(teamSelection, teamId);
    if (data) {
      setTeamTags(data);
    }
    if (error) {
      console.log("Error in loadTeamTags:", error);
      window.analytics.track("Team tag load error");
      setTeamTags([]);
      setErrors({ ...errors, loadTeamTags: true });
    }
    setLoading({ ...loading, loadTeamTags: false });
  };

  const updateData = async () => {
    const teamSelection = platform === "slack" ? "team" : "guild";
    const { data, error } = await supabase
      .from("tags")
      .select("id, name, color")
      .eq(teamSelection, teamId);
    if (data) {
      setTeamTags(data);
    }
    if (error) {
      setTeamTags([]);
    }
    setIsBulkEditModalVisible(false);
  };

  /**
   * Generates the color for a tag
   * @returns the tag color
   */
  const getNewTagColor = () => {
    let tagColor = "";
    if (teamTags.length < TAG_COLORS.length) {
      tagColor = TAG_COLORS[teamTags.length];
    } else {
      tagColor = TAG_COLORS[teamTags.length % TAG_COLORS.length];
    }
    return tagColor;
  };

  /**
   * Checks if tag is an empty string
   *
   * @param {*} tag: string - The tag to check
   * @returns  wether the tag is an empty string or not
   */
  const checkEmptyTag = (tag) => {
    const parsedTag = tag.split(" ").filter((item) => item);
    return parsedTag.length > 0;
  };

  /**
   * Adds a new tag to a given team or guild
   * @param {*} newTag - The name of the tag to be added
   */
  const addTag = async (newTag) => {
    setLoading({ ...loading, addTag: true });
    setErrors({ ...errors, addTag: false });
    const tagColor = getNewTagColor();
    const parsedTag = checkEmptyTag(newTag);
    if (parsedTag) {
      const insertTag = {
        name: newTag,
        color: tagColor,
        workspace,
      };
      if (platform === "slack") {
        insertTag.team = teamId;
      } else {
        insertTag.guild = teamId;
      }
      const { data, error } = await supabase.from("tags").insert([insertTag]);

      if (data) {
        console.log("data:", data);
        setTeamTags([...teamTags, data[0]]);
      }
      if (error) {
        setErrors({ ...errors, addTag: true });
        console.log("Error in addTag:", error);
      }
    }
    setLoading({ ...loading, addTag: false });
  };

  /**
   * Assigns tags in bulk to a list of Slack or Discord members
   * @param {*} selectedMembers - A list of Slack or Discord members
   */
  const addBulkTags = async (selectedTag, selectedMembers) => {
    setLoading({ ...loading, addBulkTags: true });
    setErrors({ ...errors, addBulkTags: false });
    const bulkInsert = selectedMembers.flatMap((member) => {
      if (member.tags) {
        if (member.tags.find((tag) => tag.id === selectedTag)) {
          return [];
        }
      }
      if (platform === "slack") {
        return [
          {
            user_id: member.slack_id,
            tag: selectedTag,
            team: teamId,
            workspace,
          },
        ];
      }
      return [
        {
          discord_member_id: member.id,
          tag: selectedTag,
          guild: teamId,
          workspace,
        },
      ];
    });
    const { data, error } = await supabase.from("user_tags").insert(bulkInsert);
    if (error) {
      setErrors({ ...errors, addBulkTags: true });
      window.analytics.track("Bulk edit tag error");
      console.error("Error in addBulkTags", error);
    }
    setLoading({ ...loading, addBulkTags: false });
  };

  useEffect(() => {
    loadTeamTags();
  }, []);

  if (!teamId) {
    return {
      loading: false,
      teamTags: [],
      setTeamTags,
      loadTeamTags,
      addTag,
      addBulkTags,
      errors,
      isBulkEditModalVisible,
      setIsBulkEditModalVisible,
      updateData,
    };
  }

  return {
    loading,
    teamTags,
    setTeamTags,
    loadTeamTags,
    addTag,
    addBulkTags,
    errors,
    isBulkEditModalVisible,
    setIsBulkEditModalVisible,
    updateData,
  };
}

export default useTags;
